import { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useSessionStorage } from 'react-use';

const KAYAK_ID_KEY = 'kayakclickid';
const IMPACT_ID_KEY = 'irclickid';
const IGNORED_UTM = 'braze';

const usePartnerReferral = () => {
  const { query } = useRouter();
  const [partnerName, setPartnerName] = useSessionStorage<'priceline' | null>(
    'partner',
    null,
  );

  const utmSources = useMemo<string[]>(() => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { utm_source } = query as { utm_source?: string | string[] };
    if (typeof utm_source === 'string') return [utm_source];
    if (Array.isArray(utm_source)) return utm_source;
    return [];
  }, [query]);

  const utmSource = utmSources[0];
  const onboardingDone = Boolean(query.onboarding_done);

  const isPriceline = utmSources.some(src => src.includes('priceline'));
  const isKayak = utmSources.some(src => src.includes('kayak'));
  const isImpact = utmSources.some(src => src.includes('impact'));

  useEffect(() => {
    if (!partnerName && isPriceline) {
      setPartnerName('priceline');
    }
  }, [partnerName, isPriceline, setPartnerName]);

  const trackingId = useMemo<string | string[] | undefined>(() => {
    if (isKayak) return query[KAYAK_ID_KEY];
    if (isImpact) return query[IMPACT_ID_KEY];
    return undefined;
  }, [query, isKayak, isImpact]);

  const hasReferral = useMemo(() => {
    const hasValidUtm = utmSources.some(src => src !== IGNORED_UTM);
    return hasValidUtm || Boolean(trackingId);
  }, [utmSources, trackingId]);

  const hasToRefineAddress = hasReferral && !query.refined_address;

  return {
    hasToRefineAddress,
    hasReferral,
    onboardingDone,
    referral: hasReferral
      ? {
          source: utmSource,
          tracking_id: trackingId,
        }
      : undefined,
    isPriceline,
    partnerName,
    setPartnerName,
  };
};

export default usePartnerReferral;
